import { CopyToClipboardButton } from '@/components/shared/CopyToClipboardButton';
import {
  createElement,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  SessionHighlightState,
  SessionTimelineHeader,
} from '@kirin/api-client/contracts/agent-sessions';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { queryKeys } from '@/hooks/api/queryKeys';
import {
  useAlertSessionSummary,
  useAlertSessionTimeline,
} from '@/hooks/api/useAgentSessionTimeline';
import { formatDateTime } from '@/lib/date-utils';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';
import { zoneName } from '@/lib/displayTimeZone';
import { uuidV4 } from '@/lib/uuid';
import {
  getPlatformIcon,
  getPlatformLabel,
} from '@/components/shared/platform/platform-utils';
import {
  PENDING_REFETCH_INTERVAL_MS,
  PENDING_REFETCH_LIMIT,
  SESSION_TIMELINE_OMISSIONS_SENTENCE,
  formatRetention,
} from './session-timeline-display';
import { KeepSessionButton } from './KeepSessionButton';
import { SessionTimelineRow } from './SessionTimelineRow';

export interface SessionTimelineDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string | null;
  occurrenceId: string;
}

const HIGHLIGHT_BANNERS: Partial<Record<SessionHighlightState, string>> = {
  pending:
    'The alert event has not arrived yet. Events are sent after the scan, so a short delay is normal.',
  not_recorded:
    'The alert event was not recorded. The agent may have been offline, restarted, or overloaded.',
  expired:
    'The alert event expired. Retention deleted the part of this session it belonged to.',
};

function HeaderFacts({
  header,
  retention,
  keep,
}: {
  header: SessionTimelineHeader;
  retention: string | null;
  /** "Keep session events", shown on the Retention line it changes. */
  keep: ReactNode;
}) {
  const timeZone = useDisplayTimeZone();
  const facts: [string, ReactNode][] = [
    [
      'Agent',
      <span className="inline-flex items-center gap-1.5">
        {createElement(getPlatformIcon(header.platform), {
          className: 'h-4 w-4 shrink-0',
          'aria-hidden': true,
        })}
        {getPlatformLabel(header.platform)}
      </span>,
    ],
    [
      'User',
      header.user
        ? (header.user.email ?? header.user.name ?? header.user.id)
        : 'Deleted user',
    ],
    [
      'Endpoint',
      header.endpoint.hostname ?? header.endpoint.machineId ?? 'Unknown',
    ],
    ['Repository', header.project?.name ?? header.repository ?? '—'],
    ['Workspace', header.workspace ?? '—'],
    ['Started', formatDateTime(header.startedAt, timeZone)],
    ['Last activity', formatDateTime(header.lastEventAt, timeZone)],
    ['Events', `${header.eventCount} total, ${header.blockedCount} blocked`],
  ];
  return (
    <dl
      className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm"
      data-testid="session-timeline-header"
    >
      {facts.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="min-w-0 truncate">{value}</dd>
        </div>
      ))}
      {/* Always present, so Keep has a home even before an expiry is known. */}
      <div className="contents">
        <dt className="self-center text-muted-foreground">Retention</dt>
        <dd className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
          <span>{retention ?? '—'}</span>
          {keep}
        </dd>
      </div>
    </dl>
  );
}

/**
 * Read-only right-side drawer with the whole agent session, opened on the
 * alert's own event (KIR-4445). Built on Dialog: there is no Sheet primitive.
 */
export function SessionTimelineDrawer({
  open,
  onOpenChange,
  organizationId,
  occurrenceId,
}: SessionTimelineDrawerProps) {
  const queryClient = useQueryClient();
  // One view id per opening, renewed during render so the first read carries it.
  const [view, setView] = useState(() => ({ open, id: uuidV4() }));
  if (view.open !== open) setView({ open, id: uuidV4() });
  const timeline = useAlertSessionTimeline(
    organizationId,
    occurrenceId,
    view.id,
    open
  );
  const summary = useAlertSessionSummary(organizationId, occurrenceId, open);
  const pages = timeline.data?.pages;
  const events = useMemo(() => pages?.flatMap(p => p.events) ?? [], [pages]);
  const firstPage = pages?.[0];

  // The header rides on the opening page only; keep it once maxPages drops it.
  const [header, setHeader] = useState<SessionTimelineHeader | null>(null);
  const headerFromPages = pages?.find(p => p.header)?.header ?? null;
  if (headerFromPages && headerFromPages !== header) {
    setHeader(headerFromPages);
  }

  const highlight = firstPage?.highlight;
  const [pendingAttempts, setPendingAttempts] = useState(0);
  const { refetch } = timeline;
  useEffect(() => {
    if (!open || highlight?.state !== 'pending') return;
    if (pendingAttempts >= PENDING_REFETCH_LIMIT) return;
    const timer = setTimeout(() => {
      setPendingAttempts(n => n + 1);
      void refetch();
    }, PENDING_REFETCH_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [open, highlight?.state, pendingAttempts, refetch]);

  const highlightState: SessionHighlightState | undefined =
    highlight?.state === 'pending' && pendingAttempts >= PENDING_REFETCH_LIMIT
      ? 'not_recorded'
      : highlight?.state;

  const highlightedEvent = events.find(e => e.eventId === highlight?.eventId);
  const alertOccurredAt = highlightedEvent?.occurredAt ?? null;

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const highlightRef = useRef<HTMLLIElement | null>(null);
  const timeZone = useDisplayTimeZone();
  const scrollPendingRef = useRef(true);
  useEffect(() => {
    if (!scrollPendingRef.current || !highlightRef.current) return;
    highlightRef.current.scrollIntoView?.({ block: 'center' });
    scrollPendingRef.current = false;
  }, [highlightedEvent]);

  const backToAlertEvent = async () => {
    if (highlightedEvent && highlightRef.current) {
      highlightRef.current.scrollIntoView?.({ block: 'center' });
      return;
    }
    scrollPendingRef.current = true;
    await queryClient.resetQueries({
      queryKey: queryKeys.sessionTimeline.timeline(
        organizationId,
        occurrenceId
      ),
    });
  };

  const banner = highlightState ? HIGHLIGHT_BANNERS[highlightState] : null;
  // The header is frozen at the opening page; the summary follows Keep and its
  // release, so it wins once loaded.
  const retained = summary.data
    ? summary.data.retained
    : header?.retained === true;
  const expiresAt = summary.data ? summary.data.expiresAt : header?.expiresAt;
  // The summary names the session even when no session row exists (events
  // pending, never recorded or expired), so the ID stays copyable.
  const sessionId = header?.sessionId ?? summary.data?.sessionId;
  const keep = (
    <KeepSessionButton
      organizationId={organizationId}
      occurrenceId={occurrenceId}
      // Either source can be stale: the summary is cached, and the header is
      // kept from the opening page. A session that exists does not revert, so
      // true from either one wins.
      recorded={header !== null || summary.data?.recorded === true}
      retained={retained}
    />
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="!left-auto !right-0 !top-0 !translate-x-0 !translate-y-0 flex h-full !max-w-2xl w-full flex-col gap-3 rounded-none border-l p-6"
        data-testid="session-timeline-drawer"
        // Default autofocus lands on Keep and opens its tooltip; focus the
        // panel instead so the tooltip shows on hover only.
        onOpenAutoFocus={e => {
          e.preventDefault();
          (e.currentTarget as HTMLElement | null)?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>Session timeline</DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-1">
            {sessionId ? (
              <>
                Session <span className="break-all font-mono">{sessionId}</span>
                <CopyToClipboardButton
                  text={sessionId}
                  variant="ghost"
                  compactCopiedState
                  className="h-6 w-6 shrink-0 p-0 text-muted-foreground hover:text-foreground"
                >
                  <span className="sr-only">Copy session ID</span>
                </CopyToClipboardButton>
              </>
            ) : (
              'Agent session activity'
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Nothing while the opening page loads, so Keep does not mount in the
            fallback and then jump to the Retention line. */}
        {header ? (
          <HeaderFacts
            header={header}
            retention={formatRetention(retained, expiresAt)}
            keep={keep}
          />
        ) : timeline.isLoading ? null : (
          <div className="flex items-start justify-between gap-4">
            <span className="text-sm text-muted-foreground">
              No session details recorded yet.
            </span>
            {keep}
          </div>
        )}

        <p
          className="text-xs text-muted-foreground"
          data-testid="session-timeline-omissions"
        >
          {SESSION_TIMELINE_OMISSIONS_SENTENCE}
        </p>

        {banner && (
          <div
            role="status"
            data-testid="session-timeline-banner"
            data-state={highlightState}
            className="rounded-md border bg-muted/40 p-3 text-sm"
          >
            {banner}
          </div>
        )}

        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">
            Ordered activity{' '}
            <span className="font-normal text-muted-foreground">
              (times in {zoneName(timeZone)})
            </span>
          </h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void backToAlertEvent()}
            disabled={highlightState !== 'found'}
          >
            Back to alert event
          </Button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          {timeline.hasPreviousPage && (
            <Button
              variant="ghost"
              size="sm"
              className="mb-2 w-full"
              disabled={timeline.isFetchingPreviousPage}
              onClick={() => void timeline.fetchPreviousPage()}
            >
              {timeline.isFetchingPreviousPage
                ? 'Loading…'
                : 'Load earlier events'}
            </Button>
          )}
          {timeline.isFetchPreviousPageError && (
            <p
              role="alert"
              className="mb-2 text-center text-xs text-destructive"
            >
              Could not load earlier events. Try again.
            </p>
          )}
          {timeline.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading timeline…</p>
          ) : timeline.isError && !timeline.data ? (
            <p className="text-sm text-destructive">
              Could not load the session timeline.
            </p>
          ) : events.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No events recorded for this session.
            </p>
          ) : (
            <ol className="flex flex-col gap-1">
              {events.map(event => {
                const isHighlight = event.eventId === highlight?.eventId;
                return (
                  <SessionTimelineRow
                    key={event.eventId}
                    ref={isHighlight ? highlightRef : undefined}
                    event={event}
                    organizationId={organizationId}
                    occurrenceId={occurrenceId}
                    highlighted={isHighlight}
                    alertOccurredAt={alertOccurredAt}
                    expanded={expanded.has(event.eventId)}
                    onToggle={() =>
                      setExpanded(prev => {
                        const next = new Set(prev);
                        if (!next.delete(event.eventId))
                          next.add(event.eventId);
                        return next;
                      })
                    }
                  />
                );
              })}
            </ol>
          )}
          {timeline.hasNextPage && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 w-full"
              disabled={timeline.isFetchingNextPage}
              onClick={() => void timeline.fetchNextPage()}
            >
              {timeline.isFetchingNextPage ? 'Loading…' : 'Load later events'}
            </Button>
          )}
          {timeline.isFetchNextPageError && (
            <p
              role="alert"
              className="mt-2 text-center text-xs text-destructive"
            >
              Could not load later events. Try again.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
